/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { SilentRequest } from '@azure/msal-browser';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/rootStore';
import apiClient from '../services/api-client';
import { passwordResetConfig } from '../features/azure-b2c/config';
import msalInstance from '../features/azure-b2c/msalInstance';
import { useSelector } from 'react-redux';
import { selectCurrentFarmerAccount } from '../features/azure-b2c/storage';
import AtomicSDK from '@atomic.io/action-cards-web-sdk';
import { clearLocalStorageExceptWhitelist } from '../store/rootReducer';
import { log } from './log';

const AZURE_B2C_FORGOT_PASSWORD_ERROR_CODE = 'AADB2C90118';

function isAuthCallbackUrl(url: string): boolean {
  try {
    const uri = new URL(url);
    if (uri.protocol === 'msauth:')
      return true;
    if (
      uri.protocol === 'https:' &&
      uri.hash &&
      (uri.hash.includes('state=') || uri.hash.includes(AZURE_B2C_FORGOT_PASSWORD_ERROR_CODE))
    )
      return true;
    return false;
  } catch {
    return false;
  }
}

function getAppHost(): string {
  const redirectUri = process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI;
  if (!redirectUri)
    return '';
  try {
    return new URL(redirectUri).host;
  } catch {
    return '';
  }
}

const useMsalFunctions = () => {
  const { instance, inProgress, accounts } = useMsal();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [msalState, setMsalState] = useState<'login' | 'reset'>('login');

  const farmerAccount = useSelector(selectCurrentFarmerAccount);

  const createTokenRequest = (prompt = 'none') => {
    const accessTokenRequest: SilentRequest = {
      scopes: [`${process.env.REACT_APP_REQUEST_API_SCOPES}`],
      account: accounts[0],
      prompt: prompt,
    };
    return accessTokenRequest;
  };

  const createCELinkTokenRequest = (prompt = 'none', accountNumber: string) => {
    // Split scopes into separate array elements for proper MSAL caching
    const ceLinkScopes = process.env.REACT_APP_REQUEST_CE_B2C_API_SCOPE
      ? ['openid', process.env.REACT_APP_REQUEST_CE_B2C_API_SCOPE]
      : ['openid'];

    const accessTokenRequest: SilentRequest = {
      scopes: ceLinkScopes,
      account: accounts[0],
      prompt: prompt,
      extraQueryParameters: {
        accountNum: accountNumber,
      },
    };
    return accessTokenRequest;
  };
  const closeBrowser = async () => {
    try {
      await Browser.close();
    } catch (error) {
      console.log("Suppressed: Tried to close browser but it wasn't open", error);
    }
  };

  const redirectCallback = useCallback(
    async (url: string) => {
      if (!url) {
        return;
      }
      if (Capacitor.isNativePlatform()) {
        await closeBrowser();
      }
      if (msalState === 'login') {
        try {
          const uri = new URL(url);
          const { hash } = uri;
          if (hash.includes(AZURE_B2C_FORGOT_PASSWORD_ERROR_CODE)) {
            setTimeout(handleResetPassword, 1000);
          }
          if (hash.includes('state=')) {
            const slug = `#state=${uri.hash.split('#state=').pop()?.replace('?', '')}`;
            if (slug) {
              await handleMsalRedirect(slug);
            }
          }
        } catch (error) {
          console.error('Error handling redirect response:', error);
          await closeBrowser();
        }
      }
      if (msalState === 'reset') {
        resetMsalInstance();
        setTimeout(handleMsalLogin, 1000);
      }
    },
    [instance, msalState],
  );

  const handleMsalRedirect = async (slug: string) => {
    if (slug) {
      await instance
        .handleRedirectPromise(slug)
        .then(async (result) => {
          if (result !== null) {
            await closeBrowser();
          } else {
            console.log('Result is null');
          }
        })
        .catch((err) => {
          console.error('Error in native callback: ');
          console.error(err);
        });
    }
  };

  const handleMsalLogin = async (callback?: () => void) => {
    try {
      setMsalState('login');
      await instance.loginRedirect(createTokenRequest('login'));
      if (callback) {
        callback();
      }
    } catch (e: any) {
      console.error('Login error:', e);
      if (e.message.includes(AZURE_B2C_FORGOT_PASSWORD_ERROR_CODE)) {
        setTimeout(handleResetPassword, 250);
      } else if (e.errorCode === 'user_cancelled') {
        resetMsalInstance();
      }
    }
  };

  const handleMsalCELogin = async (accountNumber: string, callback?: () => void) => {
    const request = createCELinkTokenRequest('login', accountNumber);
    try {
      setMsalState('login');
      await instance.loginRedirect(request);
      if (callback) {
        callback();
      }
    } catch (e: any) {
      console.error('Login error:', e);
      if (e.message.includes(AZURE_B2C_FORGOT_PASSWORD_ERROR_CODE)) {
        setTimeout(handleResetPassword, 250);
      } else if (e.errorCode === 'user_cancelled') {
        resetMsalInstance();
      }
    }
  };

  const handleMsalLogout = async () => {
    try {
      await AtomicSDK.logout();
      dispatch({
        type: 'LOGOUT_REQUEST',
      });
      await instance.logoutRedirect();
      apiClient.tokenInterceptorId = null;
      resetMsalInstance();
      navigate('/login', { replace: true });
      window.location.reload();
    } catch (e) {
      console.error('Logout error:', e);
    }
  };

  const handleCEMsalLogout = async () => {
    try {
      await AtomicSDK.logout();
      apiClient.tokenInterceptorId = null;
      resetCEMsalInstance();
      window.close();
    } catch (e) {
      console.error('Logout error:', e);
      window.close();
    }
  };

  const resetCEMsalInstance = () => {
    setMsalState('login');
    instance.getActiveAccount() && instance.setActiveAccount(null);
    instance.clearCache();
    sessionStorage.clear();
    msalInstance.clearCache();
  };

  const handleResetPassword = async () => {
    try {
      setMsalState('reset');
      await instance.loginRedirect(passwordResetConfig);
    } catch (e: any) {
      console.error('Password reset error:', e);
      if (e.errorCode === 'user_cancelled') {
        resetMsalInstance();
      }
    }
  };

  const resetMsalInstance = () => {
    setMsalState('login');
    instance.getActiveAccount() && instance.setActiveAccount(null);
    instance.clearCache();
    clearLocalStorageExceptWhitelist();
    sessionStorage.clear();
    msalInstance.clearCache();
  };

  // useEffect for In-App Browser
  // useEffect(() => {
  //   let timeout: string | number | NodeJS.Timeout | undefined
  //   Browser.addListener('browserFinished', () => {
  //     timeout = setTimeout(() => {
  //       if (
  //         accounts.length <= 0 &&
  //         !window.location.hash.includes('state') &&
  //         inProgress === 'login'
  //       ) {
  //         resetMsalInstance()
  //         window.location.reload()
  //       }
  //     }, 500)
  //   })
  //   return () => {
  //     Browser.removeAllListeners()
  //     clearTimeout(timeout)
  //   }
  // }, [accounts, inProgress])

  // Deep link handling: route in-app URLs to the router, auth URLs to MSAL.
  useEffect(() => {
    const appHost = getAppHost();

    const handleDeepLinkUrl = async (url: string) => {
      if (!url)
        return;
      if (isAuthCallbackUrl(url)) {
        await redirectCallback(url);
        return;
      }
      try {
        const uri = new URL(url);
        if (uri.protocol === 'https:' && appHost && uri.host === appHost) {
          const path = uri.pathname + uri.search;
          if (path && path !== '/') {
            navigate(path, { replace: true });
          }
        }
      } catch {
        // Ignore unparseable URLs
      }
    };

    const handleUrlOpen = async (event: { url: string }) => {
      if (isAuthCallbackUrl(event.url)) {
        await redirectCallback(event.url);
        return;
      }
      handleDeepLinkUrl(event.url);
    };

    const appUrlOpenListener = App.addListener('appUrlOpen', handleUrlOpen);

    // Cold start: app opened from link when closed — getLaunchUrl() has the URL.
    if (Capacitor.isNativePlatform()) {
      App.getLaunchUrl()
        .then((result) => {
          if (result?.url) handleDeepLinkUrl(result.url);
        })
        .catch((err) => {
          log(
            'getLaunchUrl failed or no launch URL (expected on web or when app was not opened from a link)',
            err,
          );
        });
    }

    return () => {
      appUrlOpenListener.then((listener) => {
        listener?.remove();
      });
    };
  }, [navigate, redirectCallback]);

  return {
    instance,
    inProgress,
    accounts,
    createTokenRequest,
    createCELinkTokenRequest,
    handleMsalLogin,
    handleMsalCELogin,
    handleMsalLogout,
    handleCEMsalLogout,
    resetMsalInstance,
    isAuthenticated: !!farmerAccount,
  };
};

export default useMsalFunctions;
